"use client"

import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface ActivityDay {
  date: Date
  count: number
}

interface ActivityHeatmapProps {
  title: string
  description?: string
  data: ActivityDay[]
  theme?: ThemeConfigType
  className?: string
}

export const ActivityHeatmap = observer(
  ({ title, description, data, theme, className }: ActivityHeatmapProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme
    const [hoveredDay, setHoveredDay] = useState<ActivityDay | null>(null)
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    // Auto-scroll to the right (most recent activity) on mount
    useEffect(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth
      }
    }, [])

    // Calculate the color intensity based on activity count
    // Uses progressively darker/more opaque colors like GitHub
    const getColorIntensity = useMemo(
      () =>
        (count: number, maxCount: number): string => {
          if (count === 0) return "bg-muted/30"
          const intensity = Math.ceil((count / maxCount) * 4)
          switch (intensity) {
            case 1:
              return "bg-primary/20"
            case 2:
              return "bg-primary/40"
            case 3:
              return "bg-primary/60"
            case 4:
              return "bg-primary/80"
            default:
              return "bg-primary"
          }
        },
      [],
    )

    // Generate the heatmap data for the last year (52 weeks)
    const heatmapData = useMemo(() => {
      const today = new Date()
      today.setHours(23, 59, 59, 999) // End of today
      const oneYearAgo = new Date(today)
      oneYearAgo.setFullYear(today.getFullYear() - 1)

      // Create a map of dates to counts
      const dateMap = new Map<string, number>()
      data.forEach((day) => {
        const dateKey = day.date.toISOString().split("T")[0]
        dateMap.set(dateKey, day.count)
      })

      // Find max count for color scaling
      const maxCount = Math.max(...data.map((d) => d.count), 1)

      // Generate weeks (array of weeks, each week is array of days)
      const weeks: Array<Array<{ date: Date; count: number; color: string; isFuture: boolean }>> =
        []
      const currentDate = new Date(oneYearAgo)

      // Start from the first Sunday
      while (currentDate.getDay() !== 0) {
        currentDate.setDate(currentDate.getDate() - 1)
      }

      while (currentDate <= today) {
        const week: Array<{
          date: Date
          count: number
          color: string
          isFuture: boolean
        }> = []
        for (let i = 0; i < 7; i++) {
          const date = new Date(currentDate)
          const isFuture = date > today
          const dateKey = date.toISOString().split("T")[0]
          const count = dateMap.get(dateKey) || 0
          const color = getColorIntensity(count, maxCount)
          week.push({ date, count, color, isFuture })
          currentDate.setDate(currentDate.getDate() + 1)
        }
        weeks.push(week)
      }

      return { weeks, maxCount }
    }, [data, getColorIntensity])

    const handleMouseMove = (e: React.MouseEvent, day: { date: Date; count: number }) => {
      setHoveredDay(day)
      setMousePosition({ x: e.clientX, y: e.clientY })
    }

    const handleMouseLeave = () => {
      setHoveredDay(null)
    }

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    }

    // Get month labels
    const getMonthLabels = () => {
      const labels: Array<{ month: string; col: number }> = []
      let lastMonth = -1
      heatmapData.weeks.forEach((week, idx) => {
        const month = week[0].date.getMonth()
        if (month !== lastMonth && idx > 0) {
          labels.push({
            month: week[0].date.toLocaleDateString("en-US", { month: "short" }),
            col: idx,
          })
          lastMonth = month
        }
      })
      return labels
    }

    const monthLabels = getMonthLabels()

    return (
      <Card
        className={cn(
          "w-full",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {description && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Left fade indicator - shows when there's content to the left */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-card to-transparent z-10" />

            {/* Right fade indicator - shows when there's content to the right */}
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-card to-transparent z-10" />

            <div ref={scrollContainerRef} className="relative overflow-x-auto scrollbar-hide">
              {/* Month labels */}
              <div className="relative mb-2 ml-8 flex gap-[3px] text-xs text-muted-foreground">
                <div className="flex gap-[3px]">
                  {monthLabels.map((label) => (
                    <div
                      key={`${label.month}-${label.col}`}
                      className="absolute"
                      style={{ left: `${label.col * 15}px` }}
                    >
                      {label.month}
                    </div>
                  ))}
                </div>
              </div>

              {/* Heatmap grid with day labels */}
              <div className="mt-6 flex gap-2">
                {/* Day labels on the left - grid starts with Sunday */}
                <div className="flex flex-col gap-[3px] text-xs text-muted-foreground">
                  <div className="h-3 leading-3"></div>
                  <div className="h-3 leading-3">Mon</div>
                  <div className="h-3 leading-3"></div>
                  <div className="h-3 leading-3">Wed</div>
                  <div className="h-3 leading-3"></div>
                  <div className="h-3 leading-3">Fri</div>
                  <div className="h-3 leading-3"></div>
                </div>

                {/* Heatmap grid */}
                <div className="flex gap-[3px]">
                  {heatmapData.weeks.map((week) => (
                    <div
                      key={`week-${week[0].date.toISOString()}`}
                      className="flex flex-col gap-[3px]"
                    >
                      {week.map((day) => {
                        // Don't render squares for future days
                        if (day.isFuture) {
                          return <div key={`day-${day.date.toISOString()}`} className="h-3 w-3" />
                        }

                        return (
                          <button
                            key={`day-${day.date.toISOString()}`}
                            type="button"
                            className={cn(
                              "h-3 w-3 rounded-sm border border-border transition-all hover:ring-2 hover:ring-primary",
                              day.color,
                            )}
                            onMouseMove={(e) => handleMouseMove(e, day)}
                            onMouseLeave={handleMouseLeave}
                            title={`${formatDate(day.date)}: ${day.count} activities`}
                          />
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Legend */}
              <div className="mt-4 ml-8 flex items-center gap-2 text-xs text-muted-foreground">
                <span>Less</span>
                <div className="flex gap-1">
                  <div className="h-3 w-3 rounded-sm border border-border bg-muted/30" />
                  <div className="h-3 w-3 rounded-sm border border-border bg-primary/20" />
                  <div className="h-3 w-3 rounded-sm border border-border bg-primary/40" />
                  <div className="h-3 w-3 rounded-sm border border-border bg-primary/60" />
                  <div className="h-3 w-3 rounded-sm border border-border bg-primary/80" />
                </div>
                <span>More</span>
              </div>

              {/* Tooltip */}
              {hoveredDay && (
                <div
                  className="pointer-events-none fixed z-50 rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md"
                  style={{
                    left: `${mousePosition.x + 10}px`,
                    top: `${mousePosition.y + 10}px`,
                  }}
                >
                  <div className="font-medium">{formatDate(hoveredDay.date)}</div>
                  <div className="text-muted-foreground">
                    {hoveredDay.count} {hoveredDay.count === 1 ? "activity" : "activities"}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

ActivityHeatmap.displayName = "ActivityHeatmap"
